/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Injectable} from '@angular/core';
import {environment} from '../../../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {BusinessSettingsModel} from './contracts/business-settings.model';
import {Observable} from 'rxjs';
import {CountryModel} from './contracts/country.model';
import {Cacheable} from '../../../../../decorators/cacheable.decorator';
import {TransportDocumentTypeModel} from './contracts/transport-document-type.model';
import {IntegrationChannelModel} from './contracts/integration-channel.model';
import {PeriodTypeConfigModel} from './contracts/period-type-config.model';
import {SettingQueryModel} from './contracts/setting-query.model';
import {SettingResultModel} from './contracts/setting-result.model';
import {BusinessSettingsCmdModel} from './contracts/business-settings-cmd.model';
import {SettingStatusEnum} from './contracts/setting-status.enum';
import {BusinessSettingsSubmitModel} from './contracts/business-settings-submit.model';
import {IS_TEST_ENVIRONMENT} from '../../../../../shared/utils/is-test-environment';

@Injectable({
    providedIn: 'root'
})
export class BusinessSettingsApi {

    private settingsBaseUrl = `${environment.settingsBaseUrl}/api`;

    constructor(private http: HttpClient) {
    }

    /**
     * Retrieve all Countries list.
     * @return the Countries list.
     */
    @Cacheable()
    getAllCountriesOrderedByName(): Observable<CountryModel[]> {
        return this.http.get<CountryModel[]>(`${this.settingsBaseUrl}/countries`);
    }

    /**
     * Retrieve all transport document type list.
     * @return the transport document type list.
     */
    @Cacheable()
    getAllTransportDocumentTypesOrderedByName(): Observable<TransportDocumentTypeModel[]> {
        return this.http.get<TransportDocumentTypeModel[]>(`${this.settingsBaseUrl}/transportDocumentType`);
    }

    /**
     * Retrieves all available Integration Channels.
     * @return integration channels
     */
    @Cacheable()
    getIntegrationChannels(): Observable<IntegrationChannelModel[]> {
        return this.http.get<IntegrationChannelModel[]>(`${this.settingsBaseUrl}/integrationChannel`);
    }

    /**
     * Retrieves all available Period types
     * @return Period types
     */
    @Cacheable()
    getAllPeriodTypes(): Observable<PeriodTypeConfigModel[]> {
        return this.http.get<PeriodTypeConfigModel[]>(`${this.settingsBaseUrl}/periodType`);
    }

    /**
     * Retrieve the settings info
     * For now, the schema version is hardcoded in the URL
     * @return the settings information
     */
    getSettings(businessSettingsVersion: number): Observable<BusinessSettingsModel> {
        return this.http.get<BusinessSettingsModel>(`${this.settingsBaseUrl}/schema/v1/business/${businessSettingsVersion}`);
    }

    /**
     * Updates the settings info
     * For now, the schema version is hardcoded in the URL
     * @param businessSettingsVersion the settings version
     * @param businessSettingsData the settings data
     */
    updateSettings(businessSettingsVersion: number, businessSettingsData: BusinessSettingsCmdModel): Observable<void> {
        return this.http.put<void>(`${this.settingsBaseUrl}/schema/v1/business/${businessSettingsVersion}`, businessSettingsData);
    }

    /**
     * Replicate the configuration for a specific version.
     * For now, the schema version is hardcoded in the URL
     * @param businessSettingsVersion the settings version
     * @param businessSettingsSubmitData the settings data
     */
    replicateSettings(businessSettingsVersion: number, businessSettingsSubmitData: BusinessSettingsSubmitModel): Observable<void> {
        return this.http.put<void>(`${this.settingsBaseUrl}/schema/v1/business/${businessSettingsVersion}/replicate`,
                businessSettingsSubmitData);
    }

    /**
     * Apply settings from a Draft version
     * @param businessSettingsVersion the settings version
     * @param businessSettingsSubmitData the settings notes
     */
    applySettings(businessSettingsVersion: number, businessSettingsSubmitData: BusinessSettingsSubmitModel): Observable<void> {
        return this.http.post<void>(`${this.settingsBaseUrl}/schema/v1/business/${businessSettingsVersion}/submit`,
                businessSettingsSubmitData);
    }

    /**
     * Validate settings
     * @param settings the settings
     */
    validateSettings(settings: BusinessSettingsModel): Observable<void> {
        return this.http.post<void>(`${this.settingsBaseUrl}/schema/v1/business/validate`, settings);
    }

    /**
     * Retrieves all settings
     * @return settings
     */
    searchSettings(query: SettingQueryModel): Observable<SettingResultModel> {
        let queryParameter = '';
        if (IS_TEST_ENVIRONMENT) {
            const dataCypressParameter = query.searchCriteria.settingStatusCodes.includes(SettingStatusEnum.OLD) ? 'dynamic' : 'static';
            queryParameter = `?data-cy=${dataCypressParameter}`;
        }
        return this.http.post<SettingResultModel>(`${this.settingsBaseUrl}/schema/v1/business/search${queryParameter}`, query);
    }
}
